import {useEffect, useState} from 'react';
import {Button} from '@/components/ui/button';
import {Input} from '@/components/ui/input';
import {Label} from '@/components/ui/label';
import {Badge} from '@/components/ui/badge';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle
} from '@/components/ui/dialog';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import {toast} from 'sonner';
import {Plus, Edit, Trash2, Search, X} from 'lucide-react';
import {
    getAffiliateOrders, createAffiliateOrder, updateAffiliateOrder, deleteAffiliateOrder,
    getAffiliates, getAffiliateMeta
} from '@/api/affiliate.api';

const initialForm = {
    affiliateId: '',
    affiliateName: '',
    couponCode: '',
    orderRef: '',
    orderAmount: '',
    tierTitle: '',
    cashbackPercent: '',
    cashbackAmount: '',
    status: 'Pending',
    remarks: ''
};

const statusBadgeClass = (s) => ({
    Pending: 'bg-yellow-100 text-yellow-800',
    Credited: 'bg-green-100 text-green-700',
    Cancelled: 'bg-red-100 text-red-700'
}[s] || 'bg-gray-100 text-gray-700');

const OrdersTab = () => {
    const [list, setList] = useState([]);
    const [affiliates, setAffiliates] = useState([]);
    const [statuses, setStatuses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [open, setOpen] = useState(false);
    const [editId, setEditId] = useState(null);
    const [form, setForm] = useState(initialForm);

    const load = async () => {
        try {
            const [o, a, m] = await Promise.all([getAffiliateOrders(), getAffiliates(), getAffiliateMeta()]);
            setList(o.data);
            setAffiliates(a.data);
            setStatuses(m.data.orderStatuses || []);
        } catch {
            toast.error('Failed to load orders');
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        let mounted = true;
        (async () => {
            try {
                const [o, a, m] = await Promise.all([
                    getAffiliateOrders(), getAffiliates(), getAffiliateMeta()
                ]);
                if (!mounted) return;
                setList(o.data);
                setAffiliates(a.data);
                setStatuses(m.data.orderStatuses || []);
            } catch {
                if (mounted) toast.error('Failed to load orders');
            } finally {
                if (mounted) setLoading(false);
            }
        })();
        return () => {
            mounted = false;
        };
    }, []);

    const openAdd = () => {
        setEditId(null);
        setForm(initialForm);
        setOpen(true);
    };
    const openEdit = (o) => {
        setEditId(o.id);
        setForm({
            affiliateId: o.affiliateId || '',
            affiliateName: o.affiliateName || '',
            couponCode: o.couponCode || '',
            orderRef: o.orderRef || '',
            orderAmount: o.orderAmount ?? '',
            tierTitle: o.tierTitle || '',
            cashbackPercent: o.cashbackPercent ?? '',
            cashbackAmount: o.cashbackAmount ?? '',
            status: o.status || 'Pending',
            remarks: o.remarks || ''
        });
        setOpen(true);
    };

    const onAffiliateChange = (id) => {
        const a = affiliates.find((x) => x.id === id);
        setForm({
            ...form,
            affiliateId: id,
            affiliateName: a?.fullName || '',
            couponCode: a?.couponCode || form.couponCode
        });
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!form.affiliateId) {
            toast.error('Please select an affiliate');
            return;
        }
        const payload = {
            ...form,
            orderAmount: Number(form.orderAmount) || 0,
            cashbackPercent: Number(form.cashbackPercent) || 0,
            cashbackAmount: Number(form.cashbackAmount) || 0
        };
        try {
            if (editId) {
                await updateAffiliateOrder(editId, payload);
                toast.success('Order updated');
            } else {
                await createAffiliateOrder(payload);
                toast.success('Order created');
            }
            setOpen(false);
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Save failed');
        }
    };

    const onDelete = async (id) => {
        if (!window.confirm('Delete this order?')) return;
        try {
            await deleteAffiliateOrder(id);
            toast.success('Deleted');
            load();
        } catch {
            toast.error('Delete failed');
        }
    };

    const q = search.trim().toLowerCase();
    const filtered = list.filter((o) => {
        if (statusFilter !== 'all' && o.status !== statusFilter) return false;
        if (!q) return true;
        return (
            (o.affiliateName || '').toLowerCase().includes(q) ||
            (o.couponCode || '').toLowerCase().includes(q) ||
            (o.orderRef || '').toLowerCase().includes(q)
        );
    });

    if (loading) {
        return (
            <div className="flex justify-center items-center h-48">
                <div className="animate-spin h-10 w-10 border-2 border-primary border-t-transparent rounded-full"/>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            <div className="flex justify-between items-center flex-wrap gap-3">
                <h2 className="text-xl font-semibold">Affiliate Orders</h2>
                <Button onClick={openAdd} data-testid="affordered-add-button">
                    <Plus className="mr-2" size={18}/> Add Order
                </Button>
            </div>

            <div className="flex flex-wrap gap-3 items-center">
                <div className="relative w-full sm:max-w-sm">
                    <Search size={16}
                            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"/>
                    <Input placeholder="Search by affiliate, coupon, order ref..." value={search}
                           onChange={(e) => setSearch(e.target.value)} className="pl-9 pr-9"
                           data-testid="affordered-search-input"/>
                    {search && (
                        <button type="button" onClick={() => setSearch('')}
                                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-full text-gray-400 hover:text-gray-700 hover:bg-gray-100">
                            <X size={14}/>
                        </button>
                    )}
                </div>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="w-44"><SelectValue/></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All statuses</SelectItem>
                        {statuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                </Select>
            </div>

            <div className="border rounded-lg overflow-x-auto">
                <table className="w-full min-w-[900px]">
                    <thead className="bg-gray-100">
                    <tr>
                        <th className="p-3 text-left">Affiliate</th>
                        <th className="p-3 text-left">Coupon</th>
                        <th className="p-3 text-left">Order Ref</th>
                        <th className="p-3 text-right">Order Amount</th>
                        <th className="p-3 text-left">Tier</th>
                        <th className="p-3 text-right">Cashback</th>
                        <th className="p-3 text-center">Status</th>
                        <th className="p-3 text-right">Actions</th>
                    </tr>
                    </thead>
                    <tbody>
                    {filtered.map((o) => (
                        <tr key={o.id} className="border-t hover:bg-gray-50">
                            <td className="p-3 font-medium">{o.affiliateName || '-'}</td>
                            <td className="p-3 font-mono text-sm">{o.couponCode || '-'}</td>
                            <td className="p-3 font-mono text-sm">{o.orderRef || '-'}</td>
                            <td className="p-3 text-right">₹{o.orderAmount}</td>
                            <td className="p-3 text-sm">{o.tierTitle || '-'}</td>
                            <td className="p-3 text-right">
                                <div>₹{o.cashbackAmount}</div>
                                <div className="text-xs text-gray-500">{o.cashbackPercent}%</div>
                            </td>
                            <td className="p-3 text-center">
                                <Badge className={`${statusBadgeClass(o.status)} hover:opacity-90`}>{o.status}</Badge>
                            </td>
                            <td className="p-3 text-right flex justify-end gap-2">
                                <Button size="sm" variant="outline" onClick={() => openEdit(o)}>
                                    <Edit size={14}/>
                                </Button>
                                <Button size="sm" variant="destructive" onClick={() => onDelete(o.id)}>
                                    <Trash2 size={14}/>
                                </Button>
                            </td>
                        </tr>
                    ))}
                    </tbody>
                </table>
                {filtered.length === 0 && <p className="text-center text-gray-500 p-8">No orders found.</p>}
            </div>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-xl">
                    <DialogHeader>
                        <DialogTitle>{editId ? 'Edit Order' : 'Add Order'}</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={onSubmit} className="space-y-3">
                        <div>
                            <Label>Affiliate *</Label>
                            <Select value={form.affiliateId} onValueChange={onAffiliateChange}>
                                <SelectTrigger><SelectValue placeholder="Select affiliate"/></SelectTrigger>
                                <SelectContent>
                                    {affiliates.map((a) => (
                                        <SelectItem key={a.id} value={a.id}>
                                            {a.fullName || a.couponCode || a.id}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                                <Label>Coupon Code</Label>
                                <Input value={form.couponCode}
                                       onChange={(e) => setForm({...form, couponCode: e.target.value})}/>
                            </div>
                            <div>
                                <Label>Order Ref</Label>
                                <Input value={form.orderRef}
                                       onChange={(e) => setForm({...form, orderRef: e.target.value})}/>
                            </div>
                            <div>
                                <Label>Order Amount</Label>
                                <Input type="number" min="0" value={form.orderAmount}
                                       onChange={(e) => setForm({...form, orderAmount: e.target.value})}/>
                            </div>
                            <div>
                                <Label>Tier Title</Label>
                                <Input value={form.tierTitle}
                                       onChange={(e) => setForm({...form, tierTitle: e.target.value})}/>
                            </div>
                            <div>
                                <Label>Cashback %</Label>
                                <Input type="number" min="0" step="0.01" value={form.cashbackPercent}
                                       onChange={(e) => setForm({...form, cashbackPercent: e.target.value})}/>
                            </div>
                            <div>
                                <Label>Cashback Amount</Label>
                                <Input type="number" min="0" value={form.cashbackAmount}
                                       onChange={(e) => setForm({...form, cashbackAmount: e.target.value})}/>
                            </div>
                        </div>
                        <div>
                            <Label>Status</Label>
                            <Select value={form.status} onValueChange={(v) => setForm({...form, status: v})}>
                                <SelectTrigger><SelectValue/></SelectTrigger>
                                <SelectContent>
                                    {statuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label>Remarks</Label>
                            <Input value={form.remarks} onChange={(e) => setForm({...form, remarks: e.target.value})}/>
                        </div>
                        <Button type="submit" className="w-full">
                            {editId ? 'Update Order' : 'Create Order'}
                        </Button>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default OrdersTab;
